/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core';
import { TEMPLATE_TYPE } from '@rss/common';
import clsx from 'clsx';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { get } from 'lodash';
import LogRocket from 'logrocket';
import PropTypes from 'prop-types';
import QueryString from 'query-string';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import RouteConfig from './HelpCategoryConfig';
import { KiwiNavbar } from './KiwiNavbar';
import RssLoader from './components/RssLoader';
import { PROFILE_QUERY } from './graphql/profile.query';
import { useTenants } from './hooks';
import { updateTimeoutTime } from './shared/auth.helper';
import useSecurity from './hooks/useSecurity';

const Away = lazy(() => import('./away/Routes'));
const BsasV2 = lazy(() => import('./bsas-v2'));
const Chemical = lazy(() => import('./chemical/Routes'));
const Cli = lazy(() => import('./cli/Routes'));
const AccessDenied = lazy(() => import('./components/AccessDenied/AccessDenied'));
const NotFoundPage = lazy(() => import('./containers/NotFoundPage'));
const FeatureSettings = lazy(() => import('./feature-settings/Routes'));
const HazardSign = lazy(() => import('./hazard-sign/Routes'));
const Help = lazy(() => import('./help/Routes'));
const Inventory = lazy(() => import('./inventory/Routes'));
const InternalTools = lazy(() => import('./internal-tools/Routes'));
const LocationArea = lazy(() => import('./location-area'));
const OrdersPage = lazy(() => import('./orders'));
const Nfpa = lazy(() => import('./nfpa'));
const Procedure = lazy(() => import('./procedure'));
const ProceduresLandingPage = lazy(() => import('./procedure/containers/ProceduresLandingPage'));
const Tracker = lazy(() => import('./tracker/Routes'));

const useStyles = makeStyles((theme) => ({
  ...theme.mixins.helpSidebar,
  root: {
    display: 'flex',
    flexGrow: '1',
    position: 'relative',
    width: '100%',
  },
  main: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'scroll',
    marginLeft: theme.spacing(7),
    [theme.breakpoints.up('md')]: {
      marginLeft: 0,
    },
  },
  padTop: {
    paddingTop: theme.spacing(8),
  },
  '@media print': {
    root: {
      display: 'block',
      overflowX: 'visible',
      overflowY: 'visible',
    },
    main: {
      margin: `0 ${theme.spacing(4)}px`,
      display: 'block',
      overflowX: 'visible',
      overflowY: 'visible',
    },
  },
}));

export default function App({ onUpdateIdleTimeout }) {
  const TENANTS = useTenants();
  const { browserSecurity } = useSecurity();
  const [idClaim, setIdClaim] = useState(null);

  const ldClient = useLDClient();

  const { data } = useQuery(PROFILE_QUERY, { skip: !idClaim });
  const {
    code,
    error,
    error_description: errorDesc,
    moodlesession,
    SNSession,
  } = QueryString.parse(useLocation().search);

  const classes = useStyles();

  useEffect(() => {
    if (!idClaim) {
      if (!browserSecurity.isAuthenticated) {
        browserSecurity.login();
      } else {
        setIdClaim(browserSecurity.user);
      }
    }
    return () => {};
  }, [code, error, idClaim, moodlesession, SNSession, errorDesc]);

  useEffect(() => {
    if (get(data, 'profile') && idClaim) {
      const { profile } = data;

      const { idleTimeoutInMinutes, name } = TENANTS?.getCampus(profile.campusCode) ?? { name: 'Risk & Safety' };
      if (idleTimeoutInMinutes) {
        updateTimeoutTime(Date.now());
        onUpdateIdleTimeout(idleTimeoutInMinutes);
      }

      const visitor = { id: profile.userId };
      if (!get(profile, 'features.pendo.disablePersonalInfo.value')) {
        visitor.firstName = profile.firstName;
        visitor.lastName = profile.lastName;
        visitor.email = profile.email;
      }
      // eslint-disable-next-line no-undef
      aptrinsic('identify', visitor, {
        id: profile.campusCode,
        name,
      });

      if (ldClient) {
        ldClient.identify({
          kind: 'multi',
          campus: {
            key: profile.campusCode,
            campus: name,
          },
          user: {
            key: profile.userId,
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
          },
        });
      }

      LogRocket.identify(profile.userId, {
        name: `${profile.firstName} ${profile.lastName}`,
        email: profile.email,
        campus: profile.campusCode,
      })
    }
  }, [data, idClaim, onUpdateIdleTimeout, TENANTS]);

  const profile = get(data, 'profile');

  return (
    <div className={classes.root}>
      <Suspense fallback={<RssLoader />}>
        {profile && (
          <>
            <KiwiNavbar />
            {/* Main Content : add routes to map additional content for your feature  */}
            <main className={clsx(classes.main, classes.padTop)}>
              <Switch>
                <Route path={RouteConfig.away.path} component={Away} />
                <Route path={RouteConfig.bsas.path} component={BsasV2} />
                <Route path={RouteConfig.cli.path} component={Cli} />
                <Route path="/feature-settings" component={FeatureSettings} />
                {get(profile, 'features.chemical.hazardSign.value') && (
                  <Route path={RouteConfig.hazardSign.path} component={HazardSign} />
                )}
                <Route path="/help" component={Help} />
                <Route path={RouteConfig.inventory.path} component={Inventory} />
                <Route path={RouteConfig.locationArea.path} component={LocationArea} />
                {get(profile, 'features.order.purchaseOrder.value') && (
                  <Route path={RouteConfig.orders.path} component={OrdersPage} />
                )}
                <Route path="/procedures" component={ProceduresLandingPage} />
                {get(profile, 'features.chemical.SOP.value') && (
                  <Route
                    path={RouteConfig.sop.path}
                    render={(props) => <Procedure {...props} templateType={TEMPLATE_TYPE.SOP} />}
                  />
                )}
                <Route path={RouteConfig.tracker.path} component={Tracker} />
                <Route path={RouteConfig.nfpa.path} component={Nfpa} />
                <Route path="/error" component={AccessDenied} />
                <Route path="/bands" component={Chemical} />
                <Route path="/internal" component={InternalTools} />
                {/* TODO: merge after mongo => neptune migraiton */}
                <Route component={NotFoundPage} />
              </Switch>
            </main>
          </>
        )}
      </Suspense>
    </div>
  );
}

App.propTypes = {
  onUpdateIdleTimeout: PropTypes.func.isRequired,
};
